import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';

/**
 * ModernLayout component that provides consistent header styling for the modern design
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - The page title
 * @param {boolean} props.loading - Whether the page is loading
 * @param {Function} props.onRefresh - Function to call when refresh button is clicked
 * @param {Function} props.onAdd - Function to call when add button is clicked
 * @param {string} props.addButtonText - Text for the add button
 * @param {React.ReactNode} props.actionButton - Custom action button(s) to render
 * @param {React.ReactNode} props.children - Child components to render
 */
const ModernLayout = ({ 
  title, 
  loading,
  onRefresh, 
  onAdd, 
  addButtonText = `Add ${title.replace(/s$/, '')}`,
  actionButton,
  children 
}) => {
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 }, width: '100%', boxSizing: 'border-box' }}>
      {/* Header section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        borderBottom: `1px solid ${theme => theme.palette.divider}`,
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DashboardIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="600" color="primary">
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {onRefresh && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{ borderRadius: '8px' }}
              disabled={loading}
            >
              Refresh
            </Button>
          )}
          {actionButton ? (
            actionButton
          ) : (
            onAdd && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={onAdd}
                sx={{ borderRadius: '8px' }}
              >
                {addButtonText}
              </Button>
            )
          )}
        </Box>
      </Box>

      {/* Main content */}
      {children}
    </Box>
  );
};

export default ModernLayout; 